<script lang="ts" setup>
import type {
  IRecipientInputItem,
  IRecipientInputItemNonGroup
} from '@/types/components/composer/RecipientInput';
import { isValidUrlFormat, validateEmail } from '@/helpers/urlFormat';
import { ERecipientInputItemTypes } from '@/types/components/composer/enums';
import GroupInitialsAvatar from '@/components/general/GroupInitialsAvatar.vue';
import ContactInitialsAvatar from '@/components/general/ContactInitialsAvatar.vue';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useContactsData } from '@/composables/useContactsData';
import { useI18n } from 'vue-i18n';
import { EDisabledFeatures } from '@/types/composables/useDisabledFeatureEnums';
import useDisabledFeature from '@/composables/useDisabledFeature';
import type {
  ITargetsInputModel,
  ITargetsInputProps
} from '@/types/components/settings/TargetsInput';
import { symmetricDifference } from '@/helpers/set';
import { VCombobox } from 'vuetify/components';

const targets = defineModel<ITargetsInputModel>({ required: true });
const props = defineProps<ITargetsInputProps>();

const { t } = useI18n();

const internalTargets = ref<IRecipientInputItemNonGroup[]>([]);
const targetsFocused = ref(false);
const targetsRules = ref([
  () =>
    targets.value.some((item) => !validateEmail(item) && !isValidUrlFormat(item))
      ? t('settings.general.forwarding.invalidUrl')
      : true
]);

const selectedEmailsMap = computed(() => {
  return targets.value.reduce(
    (acc, email) => {
      acc[email] = true;
      return acc;
    },
    {} as Record<string, boolean>
  );
});

const targetLabelText = computed(
  () => props.targetLabel ?? t('settings.general.forwarding.addMore')
);

const {
  userInputString,
  uniqueContactsAndRecentPartiesAndGroups,
  getContactChipName,
  getContactByEmail
} = useContactsData(selectedEmailsMap);

const { isFeatureDisabled } = useDisabledFeature();
const isTargetsFeatureDisabled = await isFeatureDisabled(EDisabledFeatures.TARGETS);

const targetsLoading = ref(false);

const resolveTargets = async () => {
  if (!targets.value.length) return;
  targetsLoading.value = true;

  const resolvedTargets = await Promise.all(
    targets.value.map<Promise<IRecipientInputItemNonGroup>>(async (email) => {
      const contact = await getContactByEmail(email);

      return {
        ...contact,
        props: { disabled: false }
      };
    })
  );

  await nextTick();
  internalTargets.value = resolvedTargets;
  targetsLoading.value = false;
};

// Helper: Filter valid unique contacts from newTargets
const filterUniqueContacts = (newTargets: string[]) => {
  return newTargets.filter(
    (email) => !internalTargets.value.some((target) => target.address === email)
  );
};

const handleTargets = async (value: (string | IRecipientInputItem)[]) => {
  if (isTargetsFeatureDisabled) return;
  const inputSplitterRegex = /[ ,;]+/;
  const newTargets = value
    .flatMap((item) => {
      if (typeof item === 'string') {
        return item.split(inputSplitterRegex);
      }

      if (item.type !== ERecipientInputItemTypes.GROUP) {
        return [item.address];
      } else {
        // Extract only unique contacts from the group
        return filterUniqueContacts(item.contacts);
      }
    })
    .map((email) => email.trim())
    .filter((email) => email);

  const validTargets = newTargets.filter(
    (target) => validateEmail(target) || isValidUrlFormat(target)
  );

  const oldSet = internalTargets.value.map((target) => target.address);

  if (
    (validTargets.length === 0 && oldSet.length === 0) ||
    symmetricDifference(validTargets, oldSet).length === 0
  ) {
    // If both sets are empty or there are no differences, no changes to process
    return;
  }

  const updatedInternalTargets = newTargets.map<IRecipientInputItemNonGroup>((email) => {
    const existingTarget = internalTargets.value.find((target) => target.address === email);

    if (existingTarget) {
      return existingTarget;
    }

    const matchedValue = value.find(
      (item): item is IRecipientInputItemNonGroup =>
        typeof item !== 'string' &&
        item.type !== ERecipientInputItemTypes.GROUP &&
        item.address === email
    );

    if (matchedValue) {
      return matchedValue;
    }
    const valid = validTargets.includes(email);
    return {
      type: ERecipientInputItemTypes.CUSTOM,
      address: email,
      hasAvatar: false,
      valid
    };
  });

  // Avoid adding duplicates to targets
  internalTargets.value = updatedInternalTargets;

  targets.value = updatedInternalTargets
    .filter((target) => target.valid)
    .map((target) => target.address);
};

const handleRemoveTarget = async (index: number) => {
  internalTargets.value.splice(index, 1);

  targets.value = internalTargets.value.map((target) => {
    return target.address;
  });
};
const targetsFocus = () => (targetsFocused.value = true);
const targetsOnBlur = () => {
  targetsFocused.value = false;
};

watch(
  () => targets.value,
  async (newTargets, oldTargets) => {
    if (
      oldTargets.some((target) => !newTargets.includes(target)) ||
      newTargets.some((target) => !oldTargets.includes(target))
    ) {
      await handleTargets(newTargets);
      await resolveTargets();
    }
  }
);

onMounted(async () => {
  if (!isTargetsFeatureDisabled) {
    await resolveTargets();
  }
});
</script>

<template>
  <v-combobox
    v-model:search="userInputString"
    :disabled="props.isUpdating || targetsLoading"
    :error-messages="props.errorMessages"
    :hide-no-data="false"
    :items="uniqueContactsAndRecentPartiesAndGroups"
    :label="props.label"
    :loading="props.isUpdating || targetsLoading"
    :menu-props="{ maxWidth: '100%' }"
    :model-value="internalTargets"
    :placeholder="t('settings.general.forwarding.placeholder')"
    :rules="targetsRules"
    active
    aria-labelledby="forwardingLabel"
    class="targets-input"
    data-test-id="targets-input"
    hide-details="auto"
    item-title="name"
    item-value="address"
    multiple
    no-filter
    return-object
    variant="outlined"
    @blur="targetsOnBlur"
    @focus="targetsFocus"
    @update:model-value="handleTargets"
  >
    <template #selection="{ internalItem: item, index }">
      <v-chip
        :class="[
          'target-chip',
          {
            'target-chip--error': !validateEmail(item.value) && !isValidUrlFormat(item.value)
          }
        ]"
        size="small"
        :color="!validateEmail(item.value) && !isValidUrlFormat(item.value) ? 'error' : ''"
      >
        <span
          class="text-body-md d-block target-chip__text"
          :title="
            item.raw.type !== ERecipientInputItemTypes.GROUP
              ? getContactChipName(item.raw.address, item.raw.name)
              : item.raw.name
          "
        >
          {{
            item.raw.type !== ERecipientInputItemTypes.GROUP
              ? getContactChipName(item.raw.address, item.raw.name)
              : item.raw.name
          }}
        </span>
        <close-btn color="on-surface-variant" @click="handleRemoveTarget(index)" />
      </v-chip>
      <span
        v-if="targets.length && targets.length == 1 && !targetsFocused"
        class="target-placeholder text-body-md"
        sentry-unmask
        >{{ targetLabelText }}</span
      >
    </template>

    <template #item="{ item, props: itemProps }">
      <v-list-item
        :key="item.type !== ERecipientInputItemTypes.GROUP ? item.address : item.name"
        :data-test-id="`targets-input-${item.type}-item-contact-${item.type !== ERecipientInputItemTypes.GROUP ? item.address : item.name}`"
        class="targets-input__item"
        no-gutters
        v-bind="itemProps"
      >
        <ContactInitialsAvatar
          v-if="item.type === ERecipientInputItemTypes.CONTACT"
          :key="`contact-${item.name}-${item.id}`"
          :contact-id="item.id"
          :has-avatar="item.hasAvatar ?? false"
          :name="item.name"
        />
        <ContactInitialsAvatar
          v-else-if="item.type === ERecipientInputItemTypes.RECENT_PARTY"
          :key="`recent-party-${item.name}-${item.id}`"
          :has-avatar="item.hasAvatar"
          :name="item.name"
        />
        <ContactInitialsAvatar
          v-else-if="item.type === ERecipientInputItemTypes.CUSTOM"
          :key="`custom-${item.name}`"
          :has-avatar="item.hasAvatar"
          :name="item.name"
        />
        <GroupInitialsAvatar
          v-else
          :key="`group-${item.name}-${item.id}`"
          :group-id="item.id"
          :has-avatar="item.hasAvatar"
          :name="item.name"
        />
        <div class="d-flex flex-column">
          <span v-if="item.name">
            {{ item.name }}
          </span>
          <span v-if="item.type !== ERecipientInputItemTypes.GROUP">
            {{ item.address }}
          </span>
          <span v-else> {{ item.contacts.join(', ') }}</span>
        </div>
      </v-list-item>
    </template>
  </v-combobox>
</template>

<style lang="scss" scoped>
.target-chip {
  height: 2.125rem !important;
  padding: 0 0 0 0.625rem !important;
  border-radius: 0.5rem !important;
  box-shadow: 0 0 0 1px rgb(var(--v-theme-outline-variant)) inset;
  background-color: rgb(var(--v-theme-surface-container));

  &--error {
    background-color: rgb(var(--v-theme-error));
    color: rgb(var(--v-theme-on-error));
  }

  &__text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.targets-input {
  &__item {
    :deep(.v-list-item__content) {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      > .v-list-item-title {
        display: none;
      }
    }

    &--invalid {
      color: rgb(var(--v-theme-error));
      text-decoration: wavy underline;

      .v-combobox__selection--selected & {
        font-weight: bold;
      }
    }
  }

  &.v-input--density-comfortable :deep(.v-field__input) {
    gap: 0.5rem;
  }

  :deep(.v-combobox__selection) {
    height: auto;
    overflow-x: hidden;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

.target-placeholder {
  display: block;
  color: rgb(var(--v-theme-on-surface-variant));
}
</style>
